import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

const CATEGORIES = ['all', 'actualite', 'evenement', 'projet'];

export default function News() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get('/articles').then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => filter === 'all' ? items : items.filter((a) => a.category === filter), [items, filter]);

  return (
    <main data-testid="news-page" className="pt-32">
      <section className="container-x max-w-7xl mx-auto pb-12">
        <FadeUp>
          <p className="overline">{t('news.overline')}</p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl text-ink tracking-tight max-w-4xl">{t('news.title')}</h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl">{t('news.intro')}</p>
        </FadeUp>
      </section>

      <section className="container-x max-w-7xl mx-auto pb-24">
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`news-filter-${c}`}
              className={`text-sm px-4 py-2 rounded-full border transition-all ${
                filter === c ? 'bg-forest text-bone border-forest' : 'bg-white text-ink-soft border-border-soft hover:border-forest hover:text-forest'
              }`}
            >
              {t(`news.${c}`)}
            </button>
          ))}
        </div>

        <Stagger className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <StaggerItem key={a.id}>
              <button onClick={() => setOpen(a)} className="text-left bg-white rounded-3xl overflow-hidden border border-border-soft hover-lift h-full w-full" data-testid={`article-${a.id}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={resolveImg(a.cover_image)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <span className="overline text-earth">{t(`news.${a.category}`)}</span>
                  <h3 className="mt-3 font-heading text-2xl text-ink leading-tight">{a[`title_${lng}`]}</h3>
                  <p className="mt-3 text-ink-soft text-sm leading-relaxed line-clamp-3">{a[`excerpt_${lng}`]}</p>
                  <span className="mt-4 inline-block text-forest text-sm font-medium">{t('common.read_more')} →</span>
                </div>
              </button>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {open && (
        <div className="fixed inset-0 z-[100] bg-ink/80 backdrop-blur-sm grid place-items-center p-4 overflow-auto" onClick={() => setOpen(null)} data-testid="article-modal">
          <article className="bg-bone max-w-3xl w-full rounded-3xl overflow-hidden my-12" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-[16/9] overflow-hidden">
              <img src={resolveImg(open.cover_image)} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="p-8 sm:p-12">
              <span className="overline text-earth">{t(`news.${open.category}`)}</span>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-ink leading-tight">{open[`title_${lng}`]}</h2>
              <div className="mt-6 prose max-w-none text-ink-soft leading-relaxed whitespace-pre-line">
                {open[`body_${lng}`]}
              </div>
              <button onClick={() => setOpen(null)} className="mt-8 btn-outline" data-testid="article-close">{t('common.close')}</button>
            </div>
          </article>
        </div>
      )}
    </main>
  );
}
